import {
	type ChangeEvent,
	type FormEvent,
	type ReactNode,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	CSSProperties,
} from 'react';

import { css } from '@compiled/react';
import { type ComponentProps } from '@uniformdev/canvas-react';
import { isRichTextValueConsideredEmpty } from '@uniformdev/richtext';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { DatePicker } from '@atlaskit/datetime-picker';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { type LocaleType } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { Button, type ButtonVariant } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { RichTextComponent } from '@/libraries/design/utils';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { isProduction } from '@/utils/urls';

import { uniformFormMessages } from './uniform-form-messages';
import {
	getFormFields,
	type FormChoiceColumns,
	type FormChoiceField,
	type FormConsentMode,
	type FormField,
} from './uniform-form-schema';
import {
	submitUniformForm,
	type FormSubmissionEndpoint,
	UniformFormSubmissionError,
} from './uniform-form-submission';
import {
	buildVisibleFormPayload,
	type FormErrors,
	type FormValue,
	type FormValues,
	validateFormConfiguration,
	validateFormValues,
} from './uniform-form-validation';

type FormAlignment = 'start' | 'center';
type FormButtonVariant = Exclude<ButtonVariant, 'secondary'>;

export interface UniformFormProps extends ComponentProps {
	customEndpoint?: string;
	submissionEndpoint?: FormSubmissionEndpoint;
	label?: string;
	formHeader?: string;
	formId?: string;
	formType?: string;
	intercomTracking?: boolean;
	intercomAppId?: string;
	consentMode?: FormConsentMode;
	legalCopyOverride?: string;
	submitLabel?: string;
	alignment?: FormAlignment;
	buttonVariant?: FormButtonVariant;
	successMessage?: unknown;
	successButtonText?: string;
	successButtonUrl?: string;
}

const DEFAULT_ANALYTICS_LABEL = 'Form';

// Until consent-config resolves, require an explicit opt-in so consent is never assumed.
const DEFAULT_CONSENT_CONFIG: LocaleType = {
	locale: 'UNKNOWN',
	localeRequiresMarketingCommunicationOptIn: true,
};

const formCss = css({
	color: wacToken('color.primary.black'),
	fontFamily: wacToken('font.charlie.text'),
	width: '100%',
});

const headingCss = css({
	color: wacToken('color.primary.black'),
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '1.5rem',
	lineHeight: '2rem',
	margin: `0 0 ${token('space.300')}`,
});
const fieldsCss = css({
	display: 'grid',
	gap: token('space.200'),
	gridTemplateColumns: '1fr',
	[media.above.md]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
});
const fullWidthCss = css({ gridColumn: '1 / -1' });
const fieldCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	minWidth: 0,
});
const labelCss = css({
	fontSize: '0.875rem',
	fontWeight: 500,
	lineHeight: '1.25rem',
});
const nameFieldsCss = css({
	display: 'grid',
	gap: token('space.200'),
	gridTemplateColumns: '1fr',
	[media.above.md]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
});
const controlCss = css({
	appearance: 'none',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: wacToken('corner.radius.card'),
	boxSizing: 'border-box',
	color: wacToken('color.primary.black'),
	display: 'block',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '1rem',
	fontWeight: 400,
	lineHeight: '1.5rem',
	margin: 0,
	maxWidth: '100%',
	minHeight: token('space.500'),
	minWidth: 0,
	padding: `0 ${token('space.150')}`,
	width: '100%',
	'&:focus': {
		borderColor: wacToken('color.primary.blue'),
		outline: 'none',
	},
	'&:focus-visible': {
		borderColor: wacToken('color.primary.blue'),
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '-2px',
	},
	'&[aria-invalid="true"]': {
		borderColor: wacToken('color.secondary.red'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.secondary.red')}`,
	},
});
const selectCss = css({
	backgroundImage:
		"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M0.282354 2.04788C-0.0941681 1.65992 -0.0941683 1.04295 0.282353 0.654992L0.623427 0.303554C1.01623 -0.101184 1.66584 -0.101184 2.05864 0.303554L6.60645 4.98954L11.1542 0.303554C11.547 -0.101184 12.1967 -0.101184 12.5895 0.303554L12.9305 0.654992C13.3071 1.04295 13.3071 1.65992 12.9305 2.04788L7.32405 7.82473C6.93125 8.22946 6.28164 8.22946 5.88884 7.82473L0.282354 2.04788Z' fill='%23101214'/%3E%3C/svg%3E\")",
	backgroundPosition: `right ${token('space.150')} center`,
	backgroundRepeat: 'no-repeat',
	backgroundSize: '14px 9px',
	paddingInlineEnd: token('space.400'),
});
const textAreaCss = css({
	minHeight: token('space.1000'),
	paddingBlock: token('space.100'),
	resize: 'vertical',
});
const requiredCss = css({
	color: wacToken('color.secondary.red'),
	display: 'inline',
	paddingInlineStart: token('space.025'),
	verticalAlign: 'baseline',
});
const helpCss = css({
	color: wacToken('color.neutral.light600'),
	fontSize: '0.75rem',
	fontWeight: 400,
	lineHeight: '1.125rem',
	margin: 0,
});
const errorCss = css({
	color: wacToken('color.secondary.red'),
	fontSize: '0.75rem',
	fontWeight: 400,
	lineHeight: '1.125rem',
	margin: 0,
});
const fieldsetCss = css({ border: 0, margin: 0, padding: 0 });
const legendCss = css({
	fontSize: '1rem',
	fontWeight: 600,
	lineHeight: '1.5rem',
	marginBlockEnd: token('space.150'),
	padding: 0,
});
const optionCss = css({
	alignItems: 'flex-start',
	breakInside: 'avoid',
	cursor: 'pointer',
	display: 'flex',
	fontSize: '0.875rem',
	gap: token('space.100'),
	lineHeight: '1.25rem',
	marginBlockStart: 0,
	marginBlockEnd: token('space.100'),
});
const radioCss = css({
	accentColor: wacToken('color.primary.blue'),
	flex: '0 0 auto',
	height: token('space.250'),
	margin: `${token('space.025')} 0 0`,
	width: token('space.250'),
});
const checkboxCss = css({
	accentColor: wacToken('color.primary.blue'),
	flex: '0 0 auto',
	height: token('space.200'),
	margin: `${token('space.025')} 0 0`,
	width: token('space.200'),
});

const choiceOptionColumnsCss = css({
	columnCount: 'var(--option-columns)',
	[media.below.sm]: {
		columnCount: 1,
	},
});

const checkboxLabelCss = css({
	alignItems: 'flex-start',
	display: 'flex',
	fontSize: '0.875rem',
	gap: token('space.100'),
	lineHeight: '1.25rem',
	marginBlockEnd: token('space.200'),
});
const conditionalFieldsCss = css({ display: 'contents' });
const honeypotCss = css({
	height: '1px',
	insetInlineStart: '-10000px',
	overflow: 'hidden',
	position: 'absolute',
	width: '1px',
});
const legalCss = css({
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
	marginBlockEnd: token('space.200'),
	marginBlockStart: token('space.300'),
});
const alertCss = css({
	color: wacToken('color.secondary.red'),
	marginBlockEnd: token('space.200'),
	marginBlockStart: token('space.200'),
});
const actionCss = css({ marginBlockStart: token('space.200') });
const successCss = css({
	color: wacToken('color.primary.black'),
	marginBlockEnd: 0,
	marginBlockStart: token('space.200'),
});

const datePickerCss = css({
	outline: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`div[class*="-control"]`]: {
		...controlCss,
		display: 'flex',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`div[data-testid*="--popper--container"] h2`]: {
		fontSize: '1rem',
	},
});

type FieldShellProps = {
	children: ReactNode;
	error?: string;
	helpText?: string;
	id: string;
	label: string;
	required?: boolean;
	width: 'full' | 'half';
};

const FieldShell = ({ children, error, helpText, id, label, required, width }: FieldShellProps) => {
	return (
		<div css={[fieldCss, width === 'full' && fullWidthCss]}>
			<label css={labelCss} htmlFor={id}>
				{label}
				{required && (
					<span aria-hidden="true" css={requiredCss}>
						*
					</span>
				)}
			</label>
			{children}
			{helpText && (
				<p css={helpCss} id={`${id}-help`}>
					{helpText}
				</p>
			)}
			{error && (
				<p css={errorCss} id={`${id}-error`}>
					{error}
				</p>
			)}
		</div>
	);
};

type RenderFieldsProps = {
	clearErrors: () => void;
	errors: FormErrors;
	fields: FormField[];
	getId: (key: string, fieldId?: string) => string;
	setValue: (key: string, value: FormValue) => void;
	values: FormValues;
};

const RenderFields = (props: RenderFieldsProps) => (
	<>
		{props.fields.map((field) => (
			<RenderField key={field.id} {...props} field={field} />
		))}
	</>
);

const RenderField = ({
	clearErrors,
	errors,
	field,
	getId,
	setValue,
	values,
}: // eslint-disable-next-line complexity -- rendering branches match the constrained field schema.
RenderFieldsProps & { field: FormField }) => {
	const intl = useIntl();
	const inputProps = (key: string, id: string, hasHelpText = false) => ({
		'aria-describedby':
			[hasHelpText ? `${id}-help` : '', errors[key] ? `${id}-error` : '']
				.filter(Boolean)
				.join(' ') || undefined,
		'aria-invalid': Boolean(errors[key]),
		'data-field': key,
		id,
		name: key,
	});
	const updateText =
		(key: string) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setValue(key, event.target.value);
			clearErrors();
		};

	if (field.type === 'formNamesFields') {
		return (
			<div css={[nameFieldsCss, field.width === 'full' && fullWidthCss]}>
				{(['firstName', 'lastName'] as const).map((key) => {
					const id = getId(key, field.id);
					const label = intl.formatMessage(
						key === 'firstName' ? uniformFormMessages.firstName : uniformFormMessages.lastName,
					);
					return (
						<FieldShell
							error={errors[key]}
							id={id}
							key={key}
							label={label}
							required={field.required}
							width="half"
						>
							<input
								{...inputProps(key, id)}
								autoComplete={key === 'firstName' ? 'given-name' : 'family-name'}
								css={controlCss}
								onChange={updateText(key)}
								required={field.required}
								type="text"
								value={String(values[key] ?? '')}
							/>
						</FieldShell>
					);
				})}
			</div>
		);
	}

	const key =
		field.type === 'formEmailField'
			? 'email'
			: field.type === 'formCompanyField'
				? 'company'
				: field.type === 'formPhoneNumberField'
					? 'number'
					: field.payloadKey;
	const id = getId(key, field.id);
	const required = field.type === 'formEmailField' || field.required;
	const label =
		field.type === 'formEmailField'
			? (field.label ?? intl.formatMessage(uniformFormMessages.email))
			: field.type === 'formCompanyField'
				? (field.label ?? intl.formatMessage(uniformFormMessages.company))
				: field.type === 'formPhoneNumberField'
					? intl.formatMessage(uniformFormMessages.phone)
					: field.label;

	if (field.type === 'formCustomChoiceField' || field.type === 'formCountryOrStateField') {
		return (
			<ChoiceField
				clearErrors={clearErrors}
				errors={errors}
				field={field}
				getId={getId}
				setValue={setValue}
				values={values}
			/>
		);
	}
	if (field.type === 'formDatepickerField') {
		return (
			<FieldShell
				error={errors[key]}
				helpText={field.helpText}
				id={id}
				label={label}
				required={required}
				width={field.width}
			>
				<div css={datePickerCss}>
					<DatePicker
						id={id}
						testId={`${field.id}-datepicker`}
						isInvalid={Boolean(errors[key])}
						locale={intl.locale}
						name={key}
						onChange={(value) => {
							setValue(key, value);
							clearErrors();
						}}
						placeholder={' '}
						selectProps={{
							'aria-invalid': Boolean(errors[key]),
							inputId: id,
							isSearchable: false,
						}}
						value={String(values[key] ?? '')}
					/>
				</div>
			</FieldShell>
		);
	}
	const isCustomText = field.type === 'formCustomTextField';
	const helpText = isCustomText ? field.helpText : undefined;
	const placeholder = isCustomText ? field.placeholder : undefined;
	const common = {
		...inputProps(key, id, Boolean(helpText)),
		autoComplete:
			field.type === 'formEmailField'
				? 'email'
				: field.type === 'formCompanyField'
					? 'organization'
					: field.type === 'formPhoneNumberField'
						? 'tel'
						: undefined,
		onChange: updateText(key),
		placeholder,
		required,
		value: String(values[key] ?? ''),
	};
	return (
		<FieldShell
			error={errors[key]}
			helpText={helpText}
			id={id}
			label={label}
			required={required}
			width={field.width}
		>
			{isCustomText && field.multiline ? (
				<textarea {...common} css={[controlCss, textAreaCss]} />
			) : (
				<input
					{...common}
					css={controlCss}
					type={
						field.type === 'formEmailField'
							? 'email'
							: field.type === 'formPhoneNumberField'
								? 'tel'
								: 'text'
					}
				/>
			)}
		</FieldShell>
	);
};

const getRadioOrCheckboxColumns = (field: FormChoiceField): FormChoiceColumns =>
	field.type === 'formCustomChoiceField' ? (field.radioOrCheckboxColumns ?? 1) : 1;

const getCustomChoiceDisplay = (field: FormChoiceField) =>
	field.type === 'formCustomChoiceField'
		? { helpText: field.helpText, placeholder: field.placeholder }
		: { helpText: undefined, placeholder: undefined };

const ChoiceField = ({
	clearErrors,
	errors,
	field,
	getId,
	setValue,
	values,
}: Omit<RenderFieldsProps, 'fields'> & { field: FormChoiceField }) => {
	const intl = useIntl();
	const id = getId(field.payloadKey, field.id);
	const { helpText, placeholder } = getCustomChoiceDisplay(field);
	const radioOrCheckboxColumns = getRadioOrCheckboxColumns(field);
	const selected = values[field.payloadKey];
	const selectedValues = Array.isArray(selected) ? selected : selected ? [selected] : [];
	const conditionalFields = field.options.flatMap((option) =>
		selectedValues.includes(option.value) ? option.conditionalFields : [],
	);
	const setChoice = (value: FormValue) => {
		setValue(field.payloadKey, value);
		clearErrors();
	};
	const describedBy =
		[helpText ? `${id}-help` : '', errors[field.payloadKey] ? `${id}-error` : '']
			.filter(Boolean)
			.join(' ') || undefined;

	return (
		<>
			<div css={[fieldCss, field.width === 'full' && fullWidthCss]}>
				{field.controlType === 'select' ? (
					<>
						<label css={labelCss} htmlFor={id}>
							{field.label}
							{field.required && (
								<span aria-hidden="true" css={requiredCss}>
									*
								</span>
							)}
						</label>
						<select
							{...{
								'aria-describedby': describedBy,
								'aria-invalid': Boolean(errors[field.payloadKey]),
							}}
							css={[controlCss, selectCss]}
							data-field={field.payloadKey}
							id={id}
							name={field.payloadKey}
							onChange={(event) => setChoice(event.target.value)}
							required={field.required}
							value={String(selected ?? '')}
						>
							<option value="">
								{placeholder ?? intl.formatMessage(uniformFormMessages.selectPlaceholder)}
							</option>
							{field.options.map((option) => (
								<option key={option.id} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</>
				) : (
					<fieldset aria-describedby={describedBy} css={fieldsetCss}>
						<legend css={legendCss}>
							{field.label}
							{field.required && (
								<span aria-hidden="true" css={requiredCss}>
									*
								</span>
							)}
						</legend>
						<div
							css={choiceOptionColumnsCss}
							style={{ '--option-columns': radioOrCheckboxColumns } as CSSProperties}
						>
							{field.options.map((option) => {
								const checked = selectedValues.includes(option.value);
								return (
									<label css={optionCss} key={option.id}>
										<input
											aria-invalid={Boolean(errors[field.payloadKey])}
											checked={checked}
											css={[
												field.controlType === 'radio' && radioCss,
												field.controlType === 'checkboxGroup' && checkboxCss,
											]}
											data-field={field.payloadKey}
											name={field.payloadKey}
											onChange={(event) =>
												setChoice(
													field.controlType === 'radio'
														? option.value
														: event.target.checked
															? [...selectedValues, option.value]
															: selectedValues.filter((value) => value !== option.value),
												)
											}
											type={field.controlType === 'radio' ? 'radio' : 'checkbox'}
											value={option.value}
										/>
										<span>{option.label}</span>
									</label>
								);
							})}
						</div>
					</fieldset>
				)}
				{helpText && (
					<p css={helpCss} id={`${id}-help`}>
						{helpText}
					</p>
				)}
				{errors[field.payloadKey] && (
					<p css={errorCss} id={`${id}-error`}>
						{errors[field.payloadKey]}
					</p>
				)}
			</div>
			{conditionalFields.length > 0 && (
				<div css={conditionalFieldsCss}>
					<RenderFields
						clearErrors={clearErrors}
						errors={errors}
						fields={conditionalFields}
						getId={getId}
						setValue={setValue}
						values={values}
					/>
				</div>
			)}
		</>
	);
};

const UniformForm = ({
	alignment = 'start',
	buttonVariant = 'primary',
	component,
	consentMode = 'none',
	customEndpoint,
	formHeader,
	formId,
	formType,
	intercomAppId,
	intercomTracking = false,
	label = DEFAULT_ANALYTICS_LABEL,
	legalCopyOverride,
	submitLabel,
	submissionEndpoint,
	successButtonText,
	successButtonUrl,
	successMessage,
}: // eslint-disable-next-line complexity -- form states and configured consent modes are intentionally explicit.
UniformFormProps) => {
	const intl = useIntl();
	const client = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fieldIdPrefix = useId().replace(/:/g, '');
	const formRef = useRef<HTMLFormElement>(null);
	const terminalRef = useRef<HTMLDivElement>(null);
	const [values, setValues] = useState<FormValues>({ alternateEmail: '', marketingOptIn: 'false' });
	const [errors, setErrors] = useState<FormErrors>({});
	const [status, setStatus] = useState<'ready' | 'submitting' | 'success' | 'error'>('ready');
	const alignedContentCss = css({ textAlign: alignment });

	const fields = useMemo(() => getFormFields(component), [component]);
	const configurationIssues = useMemo(
		() => [
			...validateFormConfiguration({
				fields,
				formId,
				formType,
				intercomAppId,
				intercomTracking,
			}),
			...(submissionEndpoint === 'custom' && !customEndpoint?.trim()
				? ['customEndpoint is required when submissionEndpoint is custom']
				: []),
		],
		[customEndpoint, fields, formId, formType, intercomAppId, intercomTracking, submissionEndpoint],
	);
	const getId = (key: string, fieldId = '') => `${fieldIdPrefix}-${fieldId}-${key}`;
	const setValue = (key: string, value: FormValue) =>
		setValues((current) => ({ ...current, [key]: value }));
	const [consentConfig, setConsentConfig] = useState<LocaleType>(DEFAULT_CONSENT_CONFIG);
	const clearErrors = () => setErrors({});

	useEffect(() => {
		if (consentMode === 'none') return;

		let isSubscribed = true;

		client
			.getConsentConfig()
			.then((config) => {
				if (!isSubscribed || !config) return;

				setConsentConfig(config);
				if (!config.localeRequiresMarketingCommunicationOptIn) {
					setValues((current) => ({ ...current, marketingOptIn: 'true' }));
				}
			})
			.catch(() => {
				// Keep DEFAULT_CONSENT_CONFIG so we never assume consent after a failed request.
			});

		return () => {
			isSubscribed = false;
		};
	}, [client, consentMode]);
	const legalText =
		legalCopyOverride ??
		(consentMode === 'privacyPolicy'
			? intl.formatMessage(uniformFormMessages.privacyPolicy)
			: consentMode === 'generalMarketingOptIn'
				? intl.formatMessage(uniformFormMessages.generalMarketingOptIn)
				: consentMode === 'termsOfService'
					? intl.formatMessage(uniformFormMessages.termsOfService)
					: consentMode === 'eapTermsOfService'
						? intl.formatMessage(uniformFormMessages.eapTermsOfService)
						: consentMode === 'eapVirtualAgentTermsAndConditions'
							? intl.formatMessage(uniformFormMessages.eapVirtualAgentTerms)
							: '');

	const fireTerminalAnalytics = (result: 'success' | 'error', failureCategory?: string) =>
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'form',
			actionSubjectId: 'form',
			source: 'uniform',
			attributes: {
				label,
				formId,
				formType,
				status: result,
				...(failureCategory ? { failureCategory } : {}),
			},
		}).fire();

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (status === 'submitting' || configurationIssues.length) return;
		if (values['alternateEmail']) return;
		const nextErrors = validateFormValues(fields, values, {
			required: intl.formatMessage(uniformFormMessages.required),
			invalidEmail: intl.formatMessage(uniformFormMessages.invalidEmail),
			businessEmail: intl.formatMessage(uniformFormMessages.businessEmail),
			firstNameRequired: intl.formatMessage(uniformFormMessages.firstNameRequired),
			lastNameRequired: intl.formatMessage(uniformFormMessages.lastNameRequired),
			companyRequired: intl.formatMessage(uniformFormMessages.companyRequired),
			phoneRequired: intl.formatMessage(uniformFormMessages.phoneRequired),
		});
		setErrors(nextErrors);
		const firstError = Object.keys(nextErrors)[0];
		if (firstError) {
			requestAnimationFrame(() =>
				formRef.current
					?.querySelector<HTMLElement>(`[data-field="${firstError}"], input[id$="-${firstError}"]`)
					?.focus(),
			);
			return;
		}
		setStatus('submitting');
		try {
			await submitUniformForm({
				client,
				customEndpoint,
				consentCheckboxValue:
					consentMode === 'generalMarketingOptIn' ? values['marketingOptIn'] === 'true' : true,
				consentMode,
				displayedLegalText: legalText,
				formId: formId ?? '',
				formType: formType ?? '',
				formValues: buildVisibleFormPayload(fields, values),
				intercomAppId,
				intercomTracking,
				locale: intl.locale,
				consentConfig,
				submissionEndpoint,
			});
			setStatus('success');
			fireTerminalAnalytics('success');
		} catch (error) {
			setStatus('error');
			fireTerminalAnalytics(
				'error',
				error instanceof UniformFormSubmissionError ? error.category : 'unknown',
			);
		} finally {
			requestAnimationFrame(() => terminalRef.current?.focus());
		}
	};

	if (configurationIssues.length) {
		if (isProduction()) return null;
		return (
			<div role="alert">
				<p>{intl.formatMessage(uniformFormMessages.configurationError)}</p>
				<code>{configurationIssues.join('; ')}</code>
			</div>
		);
	}
	if (status === 'success') {
		return (
			<div css={[successCss, alignedContentCss]} ref={terminalRef} role="status" tabIndex={-1}>
				{!isRichTextValueConsideredEmpty(successMessage) && (
					<RichTextComponent uniformParameter="successMessage" />
				)}
				{successButtonText && successButtonUrl && (
					<Button
						dataEventProps={{
							'data-product-key': 'uniform',
							'data-event-component': 'linkButton',
							'data-event-container': 'form',
							'data-event': 'clicked',
						}}
						href={successButtonUrl}
						variant={buttonVariant}
					>
						{successButtonText}
					</Button>
				)}
			</div>
		);
	}

	return (
		<TypographyStyles>
			<section css={formCss}>
				{formHeader && <h2 css={[headingCss, alignedContentCss]}>{formHeader}</h2>}
				<form
					aria-busy={status === 'submitting'}
					autoComplete="on"
					noValidate
					onSubmit={handleSubmit}
					ref={formRef}
				>
					<div css={fieldsCss}>
						<RenderFields
							clearErrors={clearErrors}
							errors={errors}
							fields={fields}
							getId={getId}
							setValue={setValue}
							values={values}
						/>
					</div>
					<div aria-hidden="true" css={honeypotCss}>
						<label htmlFor={getId('alternateEmail')}>
							{intl.formatMessage(uniformFormMessages.honeypot)}
						</label>
						<input
							autoComplete="off"
							id={getId('alternateEmail')}
							name="alternateEmail"
							onChange={(event) => setValue('alternateEmail', event.target.value)}
							tabIndex={-1}
							type="email"
							value={String(values['alternateEmail'] ?? '')}
						/>
					</div>
					{legalText && consentMode !== 'generalMarketingOptIn' && (
						<p css={[legalCss, alignedContentCss]}>{legalText}</p>
					)}
					{consentMode === 'generalMarketingOptIn' &&
						(consentConfig.localeRequiresMarketingCommunicationOptIn ? (
							<label css={checkboxLabelCss}>
								<input
									checked={values['marketingOptIn'] === 'true'}
									css={checkboxCss}
									onChange={(event) => setValue('marketingOptIn', String(event.target.checked))}
									type="checkbox"
								/>
								<span>{legalText}</span>
							</label>
						) : (
							<span css={checkboxLabelCss}>{legalText}</span>
						))}
					{status === 'error' && (
						<div css={[alertCss, alignedContentCss]} ref={terminalRef} role="alert" tabIndex={-1}>
							{intl.formatMessage(uniformFormMessages.submitError)}
						</div>
					)}
					<div css={[actionCss, alignedContentCss]}>
						<Button
							isDisabled={status === 'submitting'}
							isLoading={status === 'submitting'}
							size="large"
							type="submit"
							variant={buttonVariant}
						>
							{submitLabel ?? intl.formatMessage(uniformFormMessages.submit)}
						</Button>
					</div>
				</form>
			</section>
		</TypographyStyles>
	);
};

export default UniformForm;
